import { StorageTypeFilter } from '../../schemas/cameras';
import { Manufacturer } from '../../schemas/crm';
import { PaginatedResource } from '../../typings/Paginated';
import { translate } from '../translate';
import { BaseService, ClientError, ExtendableError, GenericPaginatedParameters, InvalidValue, NotFound, PartialBy, Unauthorized, UnexpectedError } from './base-service';


export class DuplicatedCameraValue extends ExtendableError {
    cameraId: string | undefined;
    constructor(code: string, cameraId: string) {
        super(code);
        this.cameraId = cameraId;
    }
}

export class AlreadyHasDefaultTicketReason extends ExtendableError { }

interface UpdateCameraParameters {
    title?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    serialNo?: string;
    hasFacial?: boolean;
    hasLpr?: boolean;
    stolen?: boolean;
    hasAreaInvasion?: boolean;
    isPanoramic?: boolean;
    internetType?: InternetType;
    installer?: string;
    networkType?: NetworkType;
    installedAmountCameras?: number;
    installationCompanyId?: number;
    internetCompanyId?: number;
    alertGroupId?: number | null;
    rtspAddress?: string;
    httpAddress?: string;
    sourceTech?: SourceTech;
    ptzConfiguration?: PtzConfiguration;
    riskArea: boolean;
    macNumber?: string | null;
    chipNumber?: string | null;
    shelterId?: string | null;
    manufacturer?: Manufacturer;
    videoStorageTime?: VideoStorageTime;
    storageType?: StorageTypeFilter;
    cameraConfiguration: CameraConfiguration;
}

interface CreateCameraParameters {
    title: string;
    address: string;
    latitude: number;
    longitude: number;
    serialNo: string;
    hasFacial: boolean;
    hasLpr: boolean;
    stolen: boolean;
    hasAreaInvasion: boolean;
    type: CameraType;
    internetType: InternetType;
    installer: string;
    networkType: NetworkType;
    installationCompanyId: number;
    internetCompanyId: number;
    installedAmountCameras: number;
    alertGroupId?: number | null;
    rtspAddress?: string;
    httpAddress?: string;
    ptzConfiguration?: PtzConfiguration;
    riskArea: boolean;
    macNumber?: string | null;
    chipNumber?: string | null;
    shelterId?: string | null;
    manufacturer?: Manufacturer;
    videoStorageTime: VideoStorageTime;
    storageType?: StorageTypeFilter;
    cameraConfiguration: CameraConfiguration;
}

interface GetCamerasPaginatedParameters extends GenericPaginatedParameters {
    offline?: boolean;
    showDisabled: boolean;
    hideChildren: boolean;
    type?: CameraType;
    manufacturer?: Manufacturer;
    installationCompanyId?: number;
    internetCompanyId?: number;
    storage?: StorageTypeFilter;
    serverId?: number;
    companyId?: number;
}

interface CreateAlarmCenterParameters {
    title: string;
    address: string;
    latitude: number;
    longitude: number;
    serialNo: string;
    internetType: InternetType;
    installer: string;
    networkType: NetworkType;
    installationCompanyId: number;
    internetCompanyId: number;
}

interface UpdateAlarmCenterParameters {
    title?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    serialNo?: string;
    internetType?: InternetType;
    installer?: string;
    networkType?: NetworkType;
    installationCompanyId?: number;
    internetCompanyId?: number;
}

interface UpdateCameraConfigurationParameters {
    videoTamperingSensitivity?: string | null;
    sceneChangeSensitivity?: string | null;
    defocusSensitivity?: string | null;
    invasionAreaCoordinates?: { x: number; y: number; }[] | null;
}

class CrmService extends BaseService {

    async getCamerasPaginated(params: GetCamerasPaginatedParameters): Promise<PaginatedResource<CrmPaginatedCamera>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/cameras/paginated${this.encodeQueryParams({
            ...params
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @throws {ClientError} CannotUpdateToCurrentAction
     * @returns
     */
    async resetFactory(id: string): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/camera/${id}/factory-reset`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    /**
     *
     * @throws {ClientError} CannotUpdateToCurrentAction
     * @returns
     */
    async disableEnableCamera(id: string, action: 'enable' | 'disable', reason: string): Promise<Camera> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/camera/${id}/${action}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ reason })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async updateCamera(id: string, data: UpdateCameraParameters): Promise<Camera> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/camera/${id}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(data)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            } else if (resJson.code == 'RtspAlreadyRegistered' || resJson.code == 'SerialNoAlreadyRegistered') {
                throw new DuplicatedCameraValue(resJson.code, resJson.cameraId);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async updateCameraConfiguration(id: string, data: UpdateCameraConfigurationParameters): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/internal-routes/camera/${id}/configuration`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(data)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    /**
     *
     * @throws {ClientError} RtspAlreadyRegistered | SerialNoAlreadyRegistered | RtspCameraMustHaveAnAddress
     * @returns
     */
    async createCamera(data: CreateCameraParameters): Promise<Camera> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/camera`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                ...data,
                sourceTech: 'rtsp',
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            } else if (resJson.code == 'RtspAlreadyRegistered' || resJson.code == 'SerialNoAlreadyRegistered') {
                throw new DuplicatedCameraValue(resJson.code, resJson.cameraId);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCamera(id: string): Promise<Camera> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/camera/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getCameraPassword(id: string): Promise<{ id: string; password: string; }> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/crm/camera/${id}/password`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCompaniesTicketStatus(): Promise<CompanyTicketStatus[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/companies-ticket-status`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCompaniesSimplified(): Promise<{ id: number; name: string; }[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/companies-simplified`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getKanbanTickets(params: { companyId: number; }): Promise<KanbanTicket[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/kanban-offline-events/${params.companyId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCameraHistoryPaginated(id: string, filters: {
        page: number;
        limit: number;
        fromDuration?: number;
        toDuration?: number;
        onlyOffline: boolean;
        fromStart?: number;
        toStart?: number;
        fromFinish?: number;
        toFinish?: number;
        polygonType?: string;
        polygon?: number;
    }): Promise<PaginatedResource<CameraHistoryPage>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/camera/${id}/offline-historic${this.encodeQueryParams(filters)}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOfflineHistoryPaginated(filters: {
        page: number;
        limit: number;
        fromDuration?: number;
        toDuration?: number;
        textFilter?: string;
        onlyOffline?: boolean;
        fromStart?: number;
        toStart?: number;
        fromFinish?: number;
        toFinish?: number;
        polygonType?: string;
        polygon?: number;
    }): Promise<PaginatedResource<CameraHistoryPage>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/camera-offline-historic${this.encodeQueryParams(filters)}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async downloadReportOfflineHistory(filters: {
        companyId: number;
        fromDuration?: number;
        toDuration?: number;
        onlyOffline: boolean;
        fromStart?: number;
        toStart?: number;
        fromFinish?: number;
        toFinish?: number;
        polygonType?: string;
        polygon?: number;
    }) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/crm/report/camera-offline-historic`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                ...filters,
                translations: {
                    company: translate('company'),
                    title: translate('title'),
                    address: translate('address'),
                    start: translate('beginIn'),
                    finish: translate('endIn'),
                    responsible: translate('responsible'),
                    duration: translate('duration')
                },
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.blob();
    }

    async getOfflineEvents(): Promise<OfflineEvent[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/offline-events`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getMaintenanceRequests(params: { textFilter?: string; }): Promise<MaintenanceRequestSimplified[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/maintenance-requests${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getKanbanMaintenanceRequests(params: { companyId: number; }): Promise<MaintenanceRequestSimplified[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/kanban-maintenance-requests${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async sendTicketReport(id: number, params: { reasonId: number; observation: string; }) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/offline-event/${id}/report`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getCameraSlaTemplate(ticketId: number): Promise<{ htmlContent: string; }> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/camera-sla-template/${ticketId}`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async ackOfflineEvent(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/offline-event/${id}/ack`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

    }

    async ackMaintenanceRequest(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/maintenance-request/${id}/ack`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

    }

    async getTicket(id: number): Promise<CameraHistory> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/camera-offline-historic/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getMaintenanceRequest(id: number): Promise<MaintenanceRequest> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/maintenance-request/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateCameraHistory(id: number, body: { reason: string; observation: string; }): Promise<CameraHistory> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/camera-offline-historic/${id}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(body)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateMaintenanceRequest(id: number, body: { observation: string; }): Promise<MaintenanceRequest> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/maintenance-request/${id}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(body)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteCamera(id: string) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/crm/camera/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    /**
     *
     * @throws {ClientError} SerialNoAlreadyRegistered
     * @returns
     */
    async createAlarmCenter(data: CreateAlarmCenterParameters): Promise<AlarmCenter> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/alarm-center`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                ...data,
                sourceTech: 'rtsp',
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'SerialNoAlreadyRegistered') {
                throw new DuplicatedCameraValue(resJson.code, resJson.cameraId);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAlarmCenter(id: string): Promise<AlarmCenter> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/alarm-center/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getAlarmCenterPaginated(params: GenericPaginatedParameters): Promise<PaginatedResource<AlarmCenter>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/alarm-centers/paginated${this.encodeQueryParams({
            limit: params.limit,
            page: params.page,
            textFilter: params.textFilter,
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateAlarmCenter(id: string, data: UpdateAlarmCenterParameters): Promise<AlarmCenter> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/alarm-center/${id}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(data)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'SerialNoAlreadyRegistered') {
                throw new DuplicatedCameraValue(resJson.code, resJson.cameraId);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async deleteAlarmCenter(id: string) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/alarm-center/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async downloadCameraReport(params: { offlineOnly: boolean; companyId: number; disabledOnly: boolean; withThirdParty: boolean; notIncludePanoramicChildren: boolean; wrongHttpAddressOnly: boolean; }) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/crm/cameras-report`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                ...params,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                translation: {
                    serialNo: translate('serialNo'),
                    title: translate('title'),
                    internetType: translate('internetType'),
                    createdAt: translate('createdAt'),
                    status: translate('status'),
                    type: translate('type'),
                    tags: translate('tags'),
                    installationCompany: translate('installationCompany'),
                    internetCompany: translate('internetCompany'),
                    disabled: translate('disabled'),
                    pinned: translate('pinned'),
                    panoramic: translate('panoramic'),
                    fiber: translate('fiber'),
                    latitude: translate('latitude'),
                    longitude: translate('longitude'),
                    ptzStatus: translate('ptzStatus'),
                    lastFacialEvent: translate('lastFacialEvent'),
                    lastAreaInvasionEvent: translate('lastAreaInvasionEvent'),
                    lastLprEvent: translate('lastLprEvent'),
                    stolen: translate('stolen_equipment'),
                    yes: translate('yes'),
                    no: translate('no'),
                    lastTimeOnline: translate('lastTimeOnline'),
                    lastHddValidationTime: translate('lastHddValidationTime'),
                    hasLpr: translate('lpr'),
                    hasFacial: translate('facial'),
                    hasAreaInvasion: translate('area_invasion'),
                    hasInternalAreaInvasion: translate('hasInternalAreaInvasion'),
                    hasInternalFacial: translate('hasInternalFacial'),
                    internalAreaInvasionConfidence: translate('internalAreaInvasionConfidence'),
                    address: translate('address'),
                    shelterId: translate('ShelterId'),
                    chipNumber: translate('chipNumber'),
                    macNumber: translate('macNumber'),
                    riskArea: translate('riskArea'),
                    manufacturer: translate('manufacturer'),
                    videoStorageTime: translate('videoStorageTime'),
                    networkType: translate('networkType'),
                    installedAmountCameras: translate('installedAmountCameras'),
                    updatedAt: translate('lastUpdate'),
                    installer: translate('installer'),
                    timeValidatedAt: translate('timeValidatedAt'),
                    encodeValidatedAt: translate('encodeValidatedAt'),
                    defocusValidatedAt: translate('defocusValidatedAt'),
                    overlayValidatedAt: translate('overlayValidatedAt'),
                    sceneChangeValidatedAt: translate('sceneChangeValidatedAt'),
                    recordScheduleValidatedAt: translate('recordScheduleValidatedAt'),
                    firmwareVersion: translate('firmwareVersion'),
                    resolution: translate('resolution'),
                    lprValidatedAt: translate('lprValidatedAt'),
                    facialValidatedAt: translate('facialValidatedAt'),
                    areaInvasionValidatedAt: translate('areaInvasionValidatedAt'),
                    disabledAutoConfig: translate('disabledAutoConfig'),
                    videoTamperingSensitivity: translate('videoTamperingSensitivity'),
                    sceneChangeSensitivity: translate('sceneChangeSensitivity'),
                    defocusSensitivity: translate('defocusSensitivity'),
                    disabledAt: translate('disabledAt'),
                    httpAddress: translate('httpAddress')
                }
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 204) {
            return [];
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.blob();
    }

    async createTicketReason(data: Omit<BaseTicketReason, 'id'>): Promise<TicketReason> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/crm/ticket/reason`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(data)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyHasDefaultTicketReason') {
                throw new AlreadyHasDefaultTicketReason(resJson.code);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async editTicketReason(data: PartialBy<BaseTicketReason, 'id'>): Promise<TicketReason> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/crm/ticket/reason/${data.id}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(data)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyHasDefaultTicketReason') {
                throw new AlreadyHasDefaultTicketReason(resJson.code);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getTicketReason(id: number): Promise<TicketReason> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/crm/ticket/reason/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteTicketReason(id: number): Promise<void> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/crm/ticket/reason/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getPaginatedTicketReason(params: GenericPaginatedParameters): Promise<PaginatedResource<PaginatedTicketReason>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/crm/ticket/reasons${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedTicketReason(ticketId: number): Promise<DropdownResource[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/crm/ticket/reasons-simplified${this.encodeQueryParams({ ticketId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async syncCameraConfiguration(id: string): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/camera/${id}/sync-configuration`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (!res.ok) {
            throw new UnexpectedError();
        }
    }
    async getCompaniesCameraConfigurationPaginated(params: GenericPaginatedParameters): Promise<PaginatedResource<CompanyCameraConfiguration>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/companies-camera-configuration/paginated${this.encodeQueryParams({
            limit: params.limit,
            page: params.page,
            textFilter: params.textFilter,
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCompanyCameraConfiguration(companyId: number): Promise<CompanyCameraConfiguration> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/companies-camera-configuration/${companyId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateCompanyCameraConfiguration(companyId: number, data: UpdateCompanyCameraConfigurationParameters): Promise<CompanyCameraConfiguration> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/crm/companies-camera-configuration/${companyId}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(data)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

}

export const crmService = new CrmService();
